<script setup lang="ts">
import AppModal from '@/components/generic/AppModal.vue';

interface Props {
  title?: string,
  subTitle?: string,
  backdropDismiss?: boolean,
  closeOnEscape?: boolean,
  showCloseAction?: boolean,
  canClose?: boolean,
  focusFirstInput?: boolean,
  size?: 'sm' | 'lg' | 'xl' | '2xl', // implicit default is "medium"
  id?: string,
  translucentHeader?: boolean,
  blurBackground?: boolean,
}

withDefaults(defineProps<Props>(), {
  title: undefined,
  subTitle: undefined,
  backdropDismiss: true,
  closeOnEscape: true,
  showCloseAction: true,
  canClose: true,
  focusFirstInput: false,
  size: undefined,
  id: undefined,
  translucentHeader: false,
  blurBackground: false,
});

defineEmits(['close', 'show', 'open']);
</script>

<template>
  <AppModal
    :backdrop-dismiss="backdropDismiss"
    :close-on-escape="closeOnEscape"
    :show-close-action="showCloseAction"
    :can-close="canClose"
    :focus-first-input="focusFirstInput"
    :size="size"
    :id="id"
    :translucent-header="translucentHeader"
    :blur-background="blurBackground"
    @close="$emit('close')"
    @show="$emit('show')"
    @open="$emit('open')">
    <template
      v-if="title || subTitle"
      #header>
      <div
        class="modal-title"
        v-if="title"
        :class="{ 'mr-3': showCloseAction }">
        <div v-html="title" />
        <div
          v-if="subTitle"
          class="text-default mt-1">
          {{ subTitle }}
        </div>
      </div>
    </template>

    <template
      v-if="$slots.headerActions"
      #headerActions>
      <slot name="headerActions" />
    </template>

    <template #default>
      <slot name="default" />
    </template>

    <template
      v-if="$slots.footer"
      #footer>
      <slot name="footer" />
    </template>

    <template
      v-if="$slots.actions"
      #actions>
      <slot name="actions" />
    </template>
  </AppModal>
</template>
